import React, { useEffect, useRef, useState } from 'react';
import { PostDataService } from '../services/postDataService';
import { wordDiff, DiffSegment } from '../utils/textDiff';

interface FixTyposProps {
  /** The current editor text (not the saved DB value) — unsaved edits are what get corrected. */
  text: string;
  onAccept: (corrected: string) => void;
  /** Omitted for unsaved drafts — the API then authorises via Editor role + childIds only. */
  postId?: number;
  childIds: number[];
}

/**
 * AI spell/grammar correction control: the "✨ Fix typos" button plus the
 * inline suggestion box with a word-level diff and Accept/Reject. Shared by
 * the post editor (PostsGrid) and the Review & Publish overlay. Mount with a
 * key tied to the post so state resets when the user switches posts.
 */
const FixTypos: React.FC<FixTyposProps> = ({ text, onAccept, postId, childIds }) => {
  // correctedText null = no suggestion visible.
  const [correctedText, setCorrectedText] = useState<string | null>(null);
  const [isCorrecting, setIsCorrecting] = useState(false);
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  // The text the most recent successful AI call saw / produced. Used to
  // disable the Fix-typos button until the user has edited something
  // since the last check, so we don't burn a Bedrock call asking the
  // same question twice.
  const [lastCheckedText, setLastCheckedText] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Discard any in-flight correction on unmount (post switch / cancel edit).
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  const handleFixTypos = async () => {
    if (isCorrecting) return;
    // Require some text to correct.
    if (!text.trim()) return;

    const controller = new AbortController();
    abortRef.current = controller;
    setIsCorrecting(true);
    setCorrectionError(null);
    try {
      const corrected = await PostDataService.correctText(text, {
        postId,
        childIds,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setCorrectedText(corrected);
      // Remember what we just sent so the button stays disabled until
      // the user edits something. (Updated on Accept too — see below.)
      setLastCheckedText(text);
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || controller.signal.aborted) return;
      console.error('Failed to generate correction:', err);
      setCorrectionError('Could not generate a correction. Please try again.');
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setIsCorrecting(false);
    }
  };

  const handleAccept = () => {
    if (correctedText === null) return;
    onAccept(correctedText);
    // After accept, the editor now contains the AI-cleaned text. Mark
    // that as the last-checked baseline so Fix-typos stays disabled
    // until the user changes something.
    setLastCheckedText(correctedText);
    setCorrectedText(null);
    setCorrectionError(null);
  };

  const handleReject = () => {
    setCorrectedText(null);
    setCorrectionError(null);
  };

  // The panel owns its own dismissal now (the × in its corner), so this button
  // only ever means "check this text" — no toggle state to keep tappable.
  const alreadyChecked = lastCheckedText !== null && text.trim() === lastCheckedText.trim();

  // Nothing to check: an empty box, or text unchanged since the last run. The
  // button is hidden rather than greyed — a control that can't act is noise,
  // and this one sits right under the text it would have acted on. Editing the
  // text brings it back. A run in progress still renders: that's the spinner
  // reporting on itself, not an offer.
  const notApplicable = !isCorrecting && (!text.trim() || alreadyChecked);

  // The AI handing the text back verbatim is an answer, not a suggestion:
  // there's nothing to accept and nothing to reject, so the panel reports and
  // offers no actions. The × in its corner dismisses it.
  const noChangesSuggested = correctedText !== null && correctedText.trim() === text.trim();

  return (
    <>
      {/* The whole row goes when there's nothing in it, so a hidden button
          doesn't leave its margin behind above the panel. */}
      {(!notApplicable || correctionError) && (
        <div className="d-flex align-items-center justify-content-end mb-3">
          {correctionError && (
            <span className="text-danger small me-2">{correctionError}</span>
          )}
          {/* Styling lives in `.ai-pill` (App.scss) — the hover swap it used to
              do through onMouseEnter/onMouseLeave is what :hover is for, and as
              CSS it can't get stuck mid-state when the button becomes disabled
              under the cursor. */}
          {!notApplicable && (
            <button
              type="button"
              className="ai-pill"
              data-help="posts.fix-typos"
              onClick={handleFixTypos}
              disabled={isCorrecting}
              title="Use AI to fix typos and grammar without changing meaning"
            >
              {isCorrecting ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Checking…
                </>
              ) : (
                <>✨ Fix typos <span className="ai-pill__badge">AI</span></>
              )}
            </button>
          )}
        </div>
      )}

      {correctedText !== null && (
        <div className="border rounded p-3 mb-3" style={{ background: 'rgba(99, 102, 241, 0.10)' }}>
          <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
            <span className="ai-panel__label">✨ AI spelling &amp; grammar correction</span>
            <button
              type="button"
              className="ai-panel__close"
              onClick={handleReject}
              aria-label="Hide suggestion"
              title="Hide suggestion"
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>
          {noChangesSuggested ? (
            <p className="small text-muted mb-0">No changes suggested — the text looks clean.</p>
          ) : (
            <div className="mb-2" style={{ whiteSpace: 'pre-wrap', fontSize: '0.95em', lineHeight: 1.5 }}>
              {wordDiff(text, correctedText).map((seg: DiffSegment, idx: number) => {
                if (seg.type === 'equal') return <span key={idx}>{seg.text}</span>;
                if (seg.type === 'add') return (
                  <span key={idx} style={{ background: '#d1f7d1', color: '#0f5132', borderRadius: 2, padding: '0 2px' }}>{seg.text}</span>
                );
                return (
                  <span key={idx} style={{ background: '#fde2e1', color: '#842029', borderRadius: 2, padding: '0 2px', textDecoration: 'line-through' }}>{seg.text}</span>
                );
              })}
            </div>
          )}
          {!noChangesSuggested && (
            <div className="d-flex gap-2 justify-content-end">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReject}>
                Reject
              </button>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleAccept}>
                Accept suggestion
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default FixTypos;
